import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

import { cn, useDebouncedValue } from '@/shared/libs';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ListToolbar,
  LoaderOverlay,
  Pagination,
  type PaginationPageSize,
  SimpleSelect,
  TableController,
  type TableHeader,
} from '@/shared/ui';
import {
  DEAL_SOURCE_FILTER_OPTIONS,
  DEAL_SOURCE_I18N_KEY,
  DEAL_STATUS_BADGE_CLASS,
  DEAL_STATUS_FILTER_OPTIONS,
  DEAL_STATUS_I18N_KEY,
  useDeals,
} from '@entities/Deal';
import type { DealSource, DealStatus } from '@psp-aggregator/sdk';

const PIPELINE_FETCH_LIMIT = 100;

export interface DealsPipelineProps {
  onOpenDeal: (dealId: string) => void;
}

export const DealsPipeline: FC<DealsPipelineProps> = ({ onOpenDeal }) => {
  const { i18n, t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [status, setStatus] = useState<DealStatus | ''>('');
  const [source, setSource] = useState<DealSource | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PaginationPageSize>(20);
  const dealsQuery = useDeals({
    limit: PIPELINE_FETCH_LIMIT,
    page: 1,
    source: source || undefined,
    status: status || undefined,
  });

  const filteredDeals = useMemo(() => {
    const query = debouncedSearch.toLowerCase();

    if (!query) return dealsQuery.data?.items ?? [];

    return (dealsQuery.data?.items ?? []).filter((deal) =>
      [
        deal.id,
        deal.merchant_brand,
        deal.merchant_id,
        deal.merchant_country,
        deal.merchant_currency,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [dealsQuery.data?.items, debouncedSearch]);

  const pageItems = filteredDeals.slice((page - 1) * pageSize, page * pageSize);
  const headers: TableHeader[] = [
    {
      key: true,
      title: t('pages.admin.deals.columns.merchant'),
      value: 'merchant',
    },
    { title: t('pages.admin.deals.columns.status'), value: 'status' },
    { title: t('pages.admin.deals.columns.source'), value: 'source' },
    { title: t('pages.admin.deals.columns.updated'), value: 'updated' },
    { title: '', value: 'actions' },
  ];
  const rows = pageItems.map((deal) => ({
    actions: (
      <Button
        aria-label={t('pages.admin.deals.actions.open')}
        size="icon-sm"
        type="button"
        variant="ghost"
        onClick={() => onOpenDeal(deal.id)}>
        <Eye className="h-4 w-4" />
      </Button>
    ),
    id: deal.id,
    merchant: deal.merchant_brand ?? deal.merchant_id ?? '—',
    source: t(DEAL_SOURCE_I18N_KEY[deal.source]),
    status: (
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-[10px] font-medium',
          DEAL_STATUS_BADGE_CLASS[deal.status],
        )}>
        {t(DEAL_STATUS_I18N_KEY[deal.status])}
      </span>
    ),
    updated: new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(deal.updated_at)),
  }));

  if (dealsQuery.isLoading && !dealsQuery.data) {
    return <LoaderOverlay show />;
  }

  if (dealsQuery.isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.deals.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div>
      <ListToolbar
        count={pageItems.length}
        search={{
          onChange: (value) => {
            setSearch(value);
            setPage(1);
          },
          placeholder: t('pages.admin.deals.searchPlaceholder'),
          value: search,
        }}
        total={filteredDeals.length}
      />

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <SimpleSelect
          aria-label={t('pages.admin.deals.filters.status')}
          options={[
            { label: t('pages.admin.deals.allStatuses'), value: '' },
            ...DEAL_STATUS_FILTER_OPTIONS.map((value) => ({
              label: t(DEAL_STATUS_I18N_KEY[value]),
              value,
            })),
          ]}
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <SimpleSelect
          aria-label={t('pages.admin.deals.filters.source')}
          options={[
            { label: t('pages.admin.deals.allSources'), value: '' },
            ...DEAL_SOURCE_FILTER_OPTIONS.map((value) => ({
              label: t(DEAL_SOURCE_I18N_KEY[value]),
              value,
            })),
          ]}
          value={source}
          onValueChange={(value) => {
            setSource(value);
            setPage(1);
          }}
        />
      </div>

      <TableController
        data={rows}
        emptyState={{
          filtered: Boolean(search || status || source),
          onClearFilters: () => {
            setSearch('');
            setStatus('');
            setSource('');
          },
        }}
        headers={headers}
        mobileItemConfig={{
          actionsSlot: { key: 'actions' },
          subtitleSlot: { key: 'source' },
          titleSlot: { key: 'merchant' },
          valueSlot: { key: 'status' },
        }}
        onClickRow={(_event, row) => onOpenDeal(row.id)}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={filteredDeals.length}
        onPageChange={setPage}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(1);
        }}
      />
    </div>
  );
};

DealsPipeline.displayName = 'DealsPipeline';
